import React, { useState, useEffect } from "react";
import {
  TextInput,
  Button,
  Grid,
  Select,
  Tooltip,
  Collapse,
  Group,
  Title,
  Stack,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { IconChevronUp, IconChevronDown } from "@tabler/icons-react";
import PhoneInput from "../common/PhoneInput";
import AdvancedSettingsFields from "./AdvancedSettingsFields";
import { useMediaQuery } from "@mantine/hooks";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import styles from "../settings/BusinessInfoSettings.module.css";
import {
  US_STATES,
  US_TIMEZONES,
  DEFAULT_SLOT_INTERVAL_MINS,
} from "../../utils/formConstants";
import { isDegradedSalonRecord } from "../../utils/currentLocation";

const isValidPhoneNumber = (phone) => {
  const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  return phoneRegex.test(phone);
};

function BusinessInfoForm({
  onSubmit,
  initialData,
  isEditing = false,
  showAdvancedSettings = false,
}) {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [hasChanged, setHasChanged] = useState(false);
  const [expandedAdvancedSettings, setExpandedAdvancedSettings] =
    useState(false);
  const buttonText = isEditing ? "Save" : "Continue";
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { access } = useFeatureAccess();
  // Lock business type / staffing model only when the salon is on a paid
  // subscription. Salons still on trial (or unsubscribed) can freely change
  // verticals; once they're subscribed we route changes through support so
  // the internal team can migrate the Stripe subscription accordingly.
  const hasPaidSubscription =
    !!access?.subscription_tier && access.subscription_tier !== "trial";
  const verticalLocked = isEditing && hasPaidSubscription;
  // Block saving while the current record is the minimal /access-derived one
  // (backend salon_staff join gap — see utils/currentLocation.ts). This form
  // back-fills every missing field with ""/60/false and the EDITOR'S BROWSER
  // timezone and submits them all, so saving from a degraded record would
  // overwrite the salon's real DB values with placeholders.
  const isDegradedRecord = isEditing && isDegradedSalonRecord(initialData);
  const normalizeData = (data) => {
    return {
      salon_name: data?.salon_name || "",
      legal_business_name: data?.legal_business_name || "",
      phone:
        data?.area_code && data?.phone_number
          ? `(${data?.area_code}) ${data?.phone_number?.slice(0, 3)}-${data?.phone_number?.slice(3)}`
          : "",
      streetAddress1: data?.street_address_1 || "",
      streetAddress2: data?.street_address_2 || "",
      city: data?.city || "",
      state: data?.state || "",
      zip_code: data?.zip_code || "",
      timezone:
        data?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      booking_delay_mins: data?.booking_delay_mins || 60,
      availability_slot_interval_mins:
        data?.availability_slot_interval_mins || DEFAULT_SLOT_INTERVAL_MINS,
      appts_need_accepting: data?.appts_need_accepting || false,
      appts_need_confirmation: data?.appts_need_confirmation || false,
      google_review_link: data?.google_review_link || "",
      customer_booking_horizon_days:
        data?.customer_booking_horizon_days ?? null,
      enable_discount_deduction_on_commissions:
        data?.enable_discount_deduction_on_commissions || false,
      business_vertical: data?.business_vertical || "",
      staffing_model: data?.staffing_model || "",
    };
  };

  const BUSINESS_VERTICAL_OPTIONS = [
    { value: "hair", label: "Hair Salon" },
    { value: "nail", label: "Nail Salon" },
    { value: "barber", label: "Barbershop" },
    { value: "aesthetician", label: "Aesthetician" },
    { value: "med_spa", label: "Med Spa" },
  ];

  const STAFFING_MODEL_OPTIONS = [
    { value: "full_service_w2", label: "Full-Service (W2)" },
    { value: "booth_renter_1099", label: "Booth Renter (1099)" },
    { value: "hybrid", label: "Hybrid" },
  ];

  useEffect(() => {
    if (initialData) {
      const normalizedData = normalizeData(initialData);
      setFormData({
        id: initialData?.id,
        ...normalizedData,
      });
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      const baseline = normalizeData(initialData);
      const hasAnyChange = Object.keys(baseline).some(
        (key) => baseline[key] !== next[key],
      );
      setHasChanged(hasAnyChange);
      return next;
    });

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.salon_name?.trim()) {
      newErrors.salon_name = "Business name is required";
    }
    if (!formData.phone?.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone =
        "Please enter a valid phone number (e.g., 123-456-7890)";
    }
    if (!formData.streetAddress1?.trim()) {
      newErrors.streetAddress1 = "Street address is required";
    }
    if (!formData.city?.trim()) {
      newErrors.city = "City is required";
    }
    if (!formData.state?.trim()) {
      newErrors.state = "State is required";
    }
    if (!formData.zip_code?.trim()) {
      newErrors.zip_code = "Zip code is required";
    }
    if (!formData.timezone?.trim()) {
      newErrors.timezone = "Timezone is required";
    }

    const horizon = formData.customer_booking_horizon_days;
    if (horizon !== null && horizon !== undefined && horizon !== "") {
      const n = Number(horizon);
      if (!Number.isInteger(n) || n < 0 || n > 365) {
        newErrors.customer_booking_horizon_days =
          "Booking horizon must be between 0 and 365 days (leave blank for unlimited)";
      }
    }

    // Validate google_review_link if it's not empty
    if (formData.google_review_link?.trim()) {
      if (formData.google_review_link.length > 1024) {
        newErrors.google_review_link =
          "Google review link must be less than 1024 characters";
      }
      if (!formData.google_review_link.includes("https://g.page/r")) {
        newErrors.google_review_link =
          'Link is not a valid shortened Google Review link.  Please navigate to your Google Business Profile and click "Ask for reviews" to get your link';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Never submit from a degraded record (covers Enter-key submits too).
    if (isDegradedRecord) {
      return;
    }

    if (!hasChanged && isEditing) {
      onSubmit(null);
      return;
    }

    if (validateForm()) {
      const businessData = { ...formData };

      businessData.street_address_1 = businessData.streetAddress1;
      businessData.street_address_2 = businessData.streetAddress2 || "";

      const phoneMatch = businessData.phone.match(
        /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
      );
      if (phoneMatch) {
        businessData.area_code = phoneMatch[1];
        businessData.phone_number = `${phoneMatch[2]}${phoneMatch[3]}`;
        delete businessData.phone;
      }

      delete businessData.streetAddress1;
      delete businessData.streetAddress2;

      const raw = businessData.customer_booking_horizon_days;
      const n = Number(raw);
      businessData.customer_booking_horizon_days =
        raw === null ||
        raw === undefined ||
        raw === "" ||
        !Number.isInteger(n) ||
        n < 0 ||
        n > 365
          ? null
          : n;

      onSubmit(businessData);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
      <Stack gap="md">
        <TextInput
          label="Business Name"
          required
          placeholder="Enter business name"
          value={formData?.salon_name}
          onChange={(e) => handleInputChange("salon_name", e.target.value)}
          error={errors.salon_name}
          mb="md"
        />

        <TextInput
          label="Legal Business Name"
          placeholder="Enter legal business name (optional)"
          value={formData?.legal_business_name}
          onChange={(e) =>
            handleInputChange("legal_business_name", e.target.value)
          }
          mb="md"
        />

        <Select
          label="Business Type"
          placeholder="Select business type"
          data={BUSINESS_VERTICAL_OPTIONS}
          value={formData?.business_vertical || null}
          onChange={(value) => handleInputChange("business_vertical", value)}
          clearable={!verticalLocked}
          disabled={verticalLocked}
          description={
            verticalLocked
              ? "Business type is locked while your subscription is active. Contact support to change it — we'll migrate your subscription and settings for you."
              : undefined
          }
          mb="md"
        />

        {formData?.business_vertical && (
          <Select
            label="Staffing Model"
            placeholder="Select staffing model"
            data={STAFFING_MODEL_OPTIONS}
            value={formData?.staffing_model || null}
            onChange={(value) => handleInputChange("staffing_model", value)}
            clearable={!verticalLocked}
            disabled={verticalLocked}
            description={
              verticalLocked
                ? "Staffing model is locked while your subscription is active. Contact support to change it."
                : undefined
            }
            mb="md"
          />
        )}

        <PhoneInput
          required
          value={formData?.phone}
          onChange={(value) => handleInputChange("phone", value)}
          error={errors.phone}
          mb="md"
        />

        <TextInput
          label="Street Address Line 1"
          required
          placeholder="Enter street address"
          value={formData?.streetAddress1}
          onChange={(e) => handleInputChange("streetAddress1", e.target.value)}
          error={errors.streetAddress1}
          mb="md"
        />

        <TextInput
          label="Street Address Line 2"
          placeholder="Enter street address (optional)"
          value={formData?.streetAddress2}
          onChange={(e) => handleInputChange("streetAddress2", e.target.value)}
          mb="md"
        />

        <TextInput
          label="City"
          required
          placeholder="Enter city"
          value={formData?.city}
          onChange={(e) => handleInputChange("city", e.target.value)}
          error={errors.city}
          mb="md"
        />

        <Grid mb="md">
          <Grid.Col span={6}>
            <Select
              label="State"
              required
              placeholder="Select state"
              data={US_STATES}
              value={formData?.state}
              onChange={(value) => handleInputChange("state", value)}
              error={errors.state}
              searchable
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput
              label="Zip Code"
              required
              placeholder="Zip code"
              value={formData?.zip_code}
              onChange={(e) => handleInputChange("zip_code", e.target.value)}
              error={errors.zip_code}
            />
          </Grid.Col>
        </Grid>

        <Tooltip
          label="We currently only support US-based companies. If you're from another country, please contact us at contact@getpagoda.com"
          position="top"
          multiline
          w={220}
        >
          <Select
            label="Timezone"
            required
            placeholder="Select timezone"
            data={US_TIMEZONES}
            value={formData?.timezone}
            onChange={(value) => handleInputChange("timezone", value)}
            error={errors.timezone}
            mb="md"
          />
        </Tooltip>

        <TextInput
          label={
            <Group gap="xs">
              <span>Google Review Link</span>
              <Tooltip
                label="Navigate to your Google Business Profile and click 'Ask for reviews' to get your link"
                position="top"
              >
                <IconInfoCircle
                  size="1rem"
                  style={{ cursor: "help" }}
                  color="gray"
                />
              </Tooltip>
            </Group>
          }
          placeholder="Enter Google review link (optional)"
          value={formData?.google_review_link}
          onChange={(e) =>
            handleInputChange("google_review_link", e.target.value)
          }
          error={errors.google_review_link}
          mb="md"
        />

        {showAdvancedSettings && (
          <>
            <Group
              mt="xl"
              mb="md"
              onClick={() =>
                setExpandedAdvancedSettings(!expandedAdvancedSettings)
              }
              className={styles.hoverable}
              w="fit-content"
            >
              <Title order={4} size={isMobile ? "sm" : "md"} fw={400}>
                Advanced Settings
              </Title>
              {expandedAdvancedSettings ? (
                <IconChevronUp size={16} />
              ) : (
                <IconChevronDown size={16} />
              )}
            </Group>

            <Collapse in={expandedAdvancedSettings}>
              <AdvancedSettingsFields
                formData={formData}
                onChange={handleInputChange}
                errors={errors}
              />
            </Collapse>
          </>
        )}

        <Tooltip
          label="Some of this location's settings haven't finished loading, so saving now would overwrite them with blank values. Please refresh the page and try again."
          multiline
          w={280}
          disabled={!isDegradedRecord}
        >
          <Button
            type="submit"
            fullWidth
            size="md"
            color="teal"
            mt="xl"
            mb="md"
            // data-disabled (not disabled) keeps hover events flowing so the
            // explanatory tooltip still shows; handleSubmit hard-blocks too.
            // aria-disabled mirrors it for assistive tech, which can't see
            // the visual data-disabled styling.
            data-disabled={isDegradedRecord || undefined}
            aria-disabled={isDegradedRecord || undefined}
            onClick={(e) => {
              if (isDegradedRecord) {
                e.preventDefault();
              }
            }}
          >
            {buttonText}
          </Button>
        </Tooltip>
      </Stack>
    </form>
  );
}

export default BusinessInfoForm;
